import { Box, Chip, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Typography } from '@mui/material';
import React, {useEffect, useMemo, useState} from 'react';
import CircleIcon from '@mui/icons-material/Circle';
import useMediaQuery from '@mui/material/useMediaQuery';
import { capitalise } from 'utils/stringUtil';
import {
  Payment,
  useGetPaymentsUserQuery,
  usePostMeterOrdersStatusMutation,
  usePostPaymentsReconcilePendingMutation,
} from 'redux/residentPortalApi';
import {useIsAdmin} from '../../hooks/isAdmin';
import {makeStyles} from '@mui/styles';
import {theme} from '../../theme/theme';
import ResponsiveRecordCard from './ResponsiveRecordCard';

interface OwnProps {
  propertyId: string;
}

type PaymentMetadata = {
  token?: string;
  tokenDisplay?: string;
  tokenFirst?: string;
  tokenSecond?: string;
};

const useStyles = makeStyles({
  activeStatusIcon: {
    color: 'green',
  },
  inactiveStatusIcon: {
    color: 'orange',
  },
  failedStatusIcon: {
    color: 'red',
  },
});

const PaymentHistory: React.FC<OwnProps> = ({propertyId}) => {
  const classes = useStyles();
  const isAdmin = useIsAdmin();
  const showCards = useMediaQuery(theme.breakpoints.down('md'));
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [historyFilter, setHistoryFilter] = useState('');

  const {data: paymentHistory = [], isLoading, isError, refetch} = useGetPaymentsUserQuery(
    {propertyId},
    {skip: !propertyId}
  );
  const [reconcilePendingPayments] = usePostPaymentsReconcilePendingMutation();
  const [getMeterOrdersStatus, {data: meterOrders = []}] = usePostMeterOrdersStatusMutation();

  const orderIds = useMemo(() => {
    return paymentHistory
      .map((payment) => payment.orderId)
      .filter((id): id is string => typeof id === 'string' && id.length > 0);
  }, [paymentHistory]);

  useEffect(() => {
    if (!propertyId) {
      return;
    }

    let isCancelled = false;

    const reconcilePayments = async () => {
      try {
        const result = await reconcilePendingPayments({
          body: {propertyId},
        }).unwrap();

        if (!isCancelled && (result.updatedCount ?? 0) > 0) {
          await refetch();
        }
      } catch (error) {
        console.error('Failed to reconcile pending payments for property page load:', error);
      }
    };

    void reconcilePayments();

    return () => {
      isCancelled = true;
    };
  }, [propertyId, reconcilePendingPayments, refetch]);

  useEffect(() => {
    if (orderIds.length > 0) {
      getMeterOrdersStatus({body: {orderIds}});
    }
  }, [getMeterOrdersStatus, orderIds]);

  const orderStatusMap = useMemo(() => {
    return meterOrders.reduce<Record<string, number>>((accumulator, order) => {
      if (order.id) {
        accumulator[order.id] = order.state;
      }
      return accumulator;
    }, {});
  }, [meterOrders]);

  const filteredPayments = useMemo(() => {
    const searchTerm = historyFilter.trim().toLowerCase();

    if (!searchTerm) {
      return paymentHistory;
    }

    return paymentHistory.filter((payment) => {
      const haystack = [
        payment.paymentMethod,
        payment.purpose,
        payment.paymentIntentId,
        payment.paymentDate,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(searchTerm);
    });
  }, [historyFilter, paymentHistory]);

  useEffect(() => {
    setPage(0);
  }, [filteredPayments.length]);

  const paginatedPayments = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredPayments.slice(start, start + rowsPerPage);
  }, [filteredPayments, page, rowsPerPage]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getManualToken = (payment: Payment): string | null => {
    const metadata = (payment.metadata ?? {}) as PaymentMetadata;
    const token = metadata.tokenDisplay ?? metadata.token;

    if (token && token.trim()) {
      return token.trim();
    }

    const segments = [metadata.tokenFirst, metadata.tokenSecond]
      .map((segment) => segment?.trim())
      .filter((segment): segment is string => Boolean(segment));

    return segments.length ? segments.join(' ') : null;
  };

  const renderPaymentStatus = (payment: Payment) => {
    if (!payment.orderId) {
      return null;
    }

    const status = orderStatusMap[payment.orderId ?? ''];

    if (status === undefined) {
      return (
        <Typography variant="caption" color="text.secondary">
          Checking...
        </Typography>
      );
    }

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <CircleIcon
          className={
            status === 2
              ? classes.activeStatusIcon
              : status === 0 || 1
                ? classes.inactiveStatusIcon
                : classes.failedStatusIcon
          }
          fontSize="small"
        />
        <Typography variant="caption" color="text.secondary">
          {status === 2 ? 'Successful' : status === 0 || status === 1 ? 'Pending' : 'Failed'}
        </Typography>
      </Box>
    );
  };

  const columnCount = isAdmin ? 5 : 4;

  return (
    <Paper
      elevation={3}
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        borderRadius: 2,
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
        border: '1px solid rgba(0, 0, 0, 0.05)',
        mb: 4,
      }}
    >
      <Stack spacing={{ xs: 2, sm: 3 }}>
        <Typography
          variant="h2"
          sx={{
            fontSize: { xs: '1.3rem', sm: '1.5rem', md: '1.75rem' },
            fontWeight: 600,
            color: '#1976d2',
            mb: 1,
            borderBottom: '2px solid #e0e0e0',
            paddingBottom: 1,
          }}
        >
          Payment History
        </Typography>

        <TextField
          label="Filter payments"
          placeholder="Search by purpose, method or receipt"
          size="small"
          value={historyFilter}
          onChange={(event) => setHistoryFilter(event.target.value)}
          sx={{ maxWidth: 360 }}
        />

        {showCards ? (
          <Box sx={{ display: 'grid', gap: { xs: 1.5, sm: 2 } }}>
            {isLoading ? (
              <Paper sx={{ p: 3, textAlign: 'center', borderRadius: 3 }}>
                <Typography variant="body2" color="primary" sx={{ fontWeight: 'medium' }}>
                  Loading payment history...
                </Typography>
              </Paper>
            ) : isError ? (
              <Paper sx={{ p: 3, textAlign: 'center', borderRadius: 3 }}>
                <Typography color="error" variant="body2" sx={{ fontWeight: 'medium' }}>
                  Error loading payment history.
                </Typography>
              </Paper>
            ) : filteredPayments.length > 0 ? (
              paginatedPayments.map((payment) => (
                <ResponsiveRecordCard
                  key={payment.id}
                  eyebrow="Payment"
                  title={`$${(payment.amount / 100).toFixed(2)}`}
                  badge={<Chip label={capitalise(payment.purpose)} sx={{ bgcolor: '#e3f2fd', color: '#0f265c', fontWeight: 600, maxWidth: '100%' }} />}
                  fields={[
                    {
                      label: 'Date',
                      value: (
                        <Typography sx={{ fontWeight: 600, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                          {payment.paymentDate ? new Date(payment.paymentDate).toLocaleDateString() : 'N/A'}
                        </Typography>
                      ),
                    },
                    {
                      label: 'Payment Method',
                      value: (
                        <Box>
                          <Typography sx={{ fontWeight: 600, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                            {capitalise(payment.paymentMethod ?? 'N/A')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-word' }}>
                            Receipt: {payment.paymentIntentId ?? 'N/A'}
                          </Typography>
                          {getManualToken(payment) ? (
                            <Typography variant="caption" color="primary" sx={{ display: 'block', wordBreak: 'break-word' }}>
                              Token: {getManualToken(payment)}
                            </Typography>
                          ) : null}
                        </Box>
                      ),
                    },
                    ...(isAdmin ? [{ label: 'Status', value: renderPaymentStatus(payment) }] : []),
                  ]}
                />
              ))
            ) : (
              <Paper sx={{ p: 3, textAlign: 'center', borderRadius: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  No payment history available.
                </Typography>
              </Paper>
            )}
          </Box>
        ) : (
          <TableContainer
            sx={{
              borderRadius: 1,
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
              overflow: 'hidden',
            }}
          >
            <Table sx={{ minWidth: 650 }}>
              <TableHead sx={{ backgroundColor: '#f5f7fa' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Payment Method</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Purpose</TableCell>
                  {isAdmin && <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={columnCount} sx={{ textAlign: 'center', py: 3 }}>
                      <Typography variant="body2" color="primary" sx={{ fontWeight: 'medium' }}>
                        Loading payment history...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : isError ? (
                  <TableRow>
                    <TableCell colSpan={columnCount} sx={{ textAlign: 'center', py: 3 }}>
                      <Typography color="error" variant="body2" sx={{ fontWeight: 'medium' }}>
                        Error loading payment history.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : filteredPayments.length > 0 ? (
                  paginatedPayments.map((payment) => (
                    <TableRow
                      key={payment.id}
                      sx={{
                        '&:hover': {
                          backgroundColor: '#f8f9fa',
                        },
                        transition: 'background-color 0.2s',
                      }}
                    >
                      <TableCell>
                        {payment.paymentDate ? new Date(payment.paymentDate).toLocaleDateString() : 'N/A'}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>${(payment.amount / 100).toFixed(2)}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{capitalise(payment.paymentMethod ?? 'N/A')}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Receipt: {payment.paymentIntentId ?? 'N/A'}
                        </Typography>
                        {getManualToken(payment) ? (
                          <Typography variant="caption" color="primary" sx={{ display: 'block', wordBreak: 'break-word' }}>
                            Token: {getManualToken(payment)}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>{capitalise(payment.purpose)}</TableCell>
                      {isAdmin && <TableCell>{renderPaymentStatus(payment)}</TableCell>}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={columnCount} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No payment history available.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {filteredPayments.length > 0 && (
          <TablePagination
            component="div"
            count={filteredPayments.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
          />
        )}
      </Stack>
    </Paper>
  );
};

export default PaymentHistory;
